// Premium, animated left-hand panel for the "Operate" (accounting) onboarding flow.
// Dark navy + gold brand styling with floating glassmorphic data cards.

type Props = {
  eyebrow?: string;
  headline?: React.ReactNode;
  sub?: string;
};

export default function OperateAside({
  eyebrow = 'CONNCCT · ACCOUNTING',
  headline = <>The business side of your startup, <span style={{ color: '#FFD54F' }}>handled.</span></>,
  sub = 'Bookkeeping, VAT and UAE corporate tax, filed on time, every time, never a missed FTA deadline.',
}: Props) {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: 'linear-gradient(165deg,#0a1826 0%,#122a41 52%,#0a1826 100%)' }}>
      <style>{`
        @keyframes oaFloat  { 0%,100%{transform:translateY(0)}   50%{transform:translateY(-11px)} }
        @keyframes oaFloat2 { 0%,100%{transform:translateY(0)}   50%{transform:translateY(9px)} }
        @keyframes oaRise   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }
        @keyframes oaRing   { from{stroke-dashoffset:339} to{stroke-dashoffset:27} }
        @keyframes oaGlow   { 0%,100%{opacity:.45} 50%{opacity:.9} }
        @keyframes oaBar    { from{transform:scaleY(0)} to{transform:scaleY(1)} }
        @keyframes oaPulse  { 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,.45)} 50%{box-shadow:0 0 0 6px rgba(34,197,94,0)} }
      `}</style>

      {/* Ambient glow blobs */}
      <div style={{ position: 'absolute', width: 440, height: 440, borderRadius: '50%', top: -140, right: -130, background: 'radial-gradient(circle,rgba(255,213,79,.18),transparent 65%)', animation: 'oaGlow 6s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', bottom: -130, left: -110, background: 'radial-gradient(circle,rgba(56,132,255,.16),transparent 65%)', animation: 'oaGlow 7s 1s ease-in-out infinite' }} />
      {/* Faint grid texture */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.5, backgroundImage: 'linear-gradient(rgba(255,255,255,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.035) 1px,transparent 1px)', backgroundSize: '38px 38px' }} />

      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '56px 48px' }}>
        {/* The panel is a half-width column, so on a wide screen it is far wider
            than the 440px composition inside it. Centre the block as a unit —
            centring each child instead would break the shared left edge that
            lines the eyebrow, headline and cards up. */}
        <div style={{ width: '100%', maxWidth: 440, marginInline: 'auto', display: 'flex', flexDirection: 'column' }}>
        <span style={{ display: 'inline-block', width: 'fit-content', fontSize: 11, fontWeight: 800, letterSpacing: '.18em', color: 'rgba(255,213,79,.9)', border: '1px solid rgba(255,213,79,.3)', borderRadius: 999, padding: '6px 14px', marginBottom: 22, animation: 'oaRise .6s .05s both' }}>
          {eyebrow}
        </span>
        <h2 style={{ fontSize: 34, lineHeight: 1.15, fontWeight: 900, color: '#fff', letterSpacing: '-.02em', margin: 0, maxWidth: 420, animation: 'oaRise .6s .12s both' }}>
          {headline}
        </h2>
        <p style={{ color: 'rgba(255,255,255,.55)', fontSize: 14.5, lineHeight: 1.6, margin: '16px 0 0', maxWidth: 380, animation: 'oaRise .6s .2s both' }}>
          {sub}
        </p>

        {/* Floating data composition */}
        <div style={{ position: 'relative', marginTop: 44, height: 230, maxWidth: 440 }}>
          {/* Main glass card — compliance score + checklist */}
          <div style={{ position: 'absolute', left: 0, top: 0, width: 300, background: 'rgba(255,255,255,.07)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,.13)', borderRadius: 20, padding: '20px 22px', boxShadow: '0 30px 60px rgba(0,0,0,.45)', animation: 'oaRise .7s .3s both, oaFloat 6s 1s ease-in-out infinite' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              {/* Score ring */}
              <div style={{ position: 'relative', width: 76, height: 76, flexShrink: 0 }}>
                <svg width="76" height="76" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="11" />
                  <circle cx="60" cy="60" r="54" fill="none" stroke="#FFD54F" strokeWidth="11" strokeLinecap="round"
                    strokeDasharray="339" strokeDashoffset="27" transform="rotate(-90 60 60)"
                    style={{ animation: 'oaRing 1.3s 0.6s cubic-bezier(.4,0,.2,1) both' }} />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 19, fontWeight: 900, color: '#fff', lineHeight: 1 }}>92%</span>
                </div>
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,.5)', margin: 0, textTransform: 'uppercase', letterSpacing: '.06em' }}>Compliance</p>
                <p style={{ fontSize: 15, fontWeight: 800, color: '#fff', margin: '3px 0 0' }}>Investor-ready</p>
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {[['VAT return Q1', 'Filed'], ['Corporate tax', 'On track'], ['Bookkeeping', 'Reconciled']].map(([label, status], i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(34,197,94,.2)', border: '1px solid rgba(34,197,94,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.8)', fontWeight: 600, flex: 1 }}>{label}</span>
                  <span style={{ fontSize: 10.5, color: '#4ade80', fontWeight: 700 }}>{status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Floating mini bar-chart card */}
          <div style={{ position: 'absolute', right: 0, top: 18, width: 158, background: 'rgba(255,255,255,.07)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,.13)', borderRadius: 18, padding: '16px 16px 14px', boxShadow: '0 24px 48px rgba(0,0,0,.4)', animation: 'oaRise .7s .5s both, oaFloat2 5s 1.4s ease-in-out infinite' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.5)', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Cash flow</p>
            <p style={{ fontSize: 17, fontWeight: 900, color: '#fff', margin: '0 0 12px' }}>AED 2.4M</p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 50 }}>
              {[40, 62, 48, 78, 58, 92].map((h, i) => (
                <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: 4, transformOrigin: 'bottom', background: i === 5 ? 'linear-gradient(180deg,#FFD54F,#E6B400)' : 'rgba(255,255,255,.18)', animation: `oaBar .6s ${0.7 + i * 0.08}s cubic-bezier(.4,0,.2,1) both` }} />
              ))}
            </div>
          </div>

          {/* Floating "filed" pill */}
          <div style={{ position: 'absolute', right: 34, bottom: 0, display: 'flex', alignItems: 'center', gap: 9, background: 'rgba(255,255,255,.95)', borderRadius: 14, padding: '10px 14px', boxShadow: '0 18px 40px rgba(0,0,0,.35)', animation: 'oaRise .7s .7s both, oaFloat 5.5s 1.8s ease-in-out infinite' }}>
            <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'oaPulse 2.4s ease-in-out infinite' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
            <div>
              <p style={{ fontSize: 12, fontWeight: 800, color: '#0a1826', margin: 0, whiteSpace: 'nowrap' }}>Filed with the FTA</p>
              <p style={{ fontSize: 10.5, color: '#6b7280', margin: 0, whiteSpace: 'nowrap' }}>2 days early</p>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
